/**
 * Feed API client (PRD 2, Story 2.4.3 + E17.1.x rich content/threads).
 *
 * Cliente fino sobre a instância axios pré-configurada em
 * ``services/api.ts`` (JWT Bearer + X-Company-Slug + interceptor de
 * 401). Cada método mapeia 1:1 com um endpoint do backend
 * ``V1/backend/app/api/v1/endpoints/feed.py``.
 *
 * Não cuida de cache nem de WebSocket — isso vive nos hooks
 * (``useFeedTimeline`` / ``usePostComments``).
 */

import api from "./api";
import {
  FeedAttachment,
  FeedBuscaPagina,
  FeedComment,
  FeedCommentMention,
  FeedCursor,
  FeedMentionCandidate,
  FeedPinContext,
  FeedPinScope,
  FeedPost,
  FeedReaction,
  FeedReactionToggleResponse,
  FeedSearchParams,
  FeedTimelineLote,
  FeedAnnouncementInput,
  FeedAppreciationInput,
  FeedEventInput,
  FeedPostType,
  FeedVisibilityScope,
  Poll,
  TrendingHashtag,
} from "@/types/feed";

const FEED_BASE = "/feed";

/** O número de posts novos do menu lateral (spec do número de novidades, §5.2). */
export interface NovidadesDoFeed {
  total: number;
}

export interface ListPostsParams {
  before?: string;
  limit?: number;
  include_deleted?: boolean;
}

/** Enquete a anexar no create do post (E17.2.1): pergunta + opções (2..10). */
export interface PollCreateInput {
  question: string;
  options: string[];
}

/** Payload de criação de post (E17.1.1 + E17.2.1): conteúdo + anexos + escopo + enquete. */
export interface FeedPostCreateInput {
  content: string;
  /** Tipo do post (E17.3.2). Omitido → backend infere (poll se houver enquete). */
  post_type?: FeedPostType;
  attachments?: FeedAttachment[];
  visibility_scope?: FeedVisibilityScope;
  scope_target_id?: string | null;
  poll?: PollCreateInput;
  /** Evento (E17.3.3) — presente → post_type='event' + CalendarEvent ligado. */
  event?: FeedEventInput;
  /** Elogio (E17.3.4) — presente → post_type='appreciation' (badge + destinatários). */
  appreciation?: FeedAppreciationInput;
  /** Anúncio (E17.3.5) — presente → post_type='announcement' (fica no topo até expires_at). */
  announcement?: FeedAnnouncementInput;
  /** @menções no corpo do post (E17.3.2b) — cada mencionado recebe feed.mentioned. */
  mentions?: FeedCommentMention[];
  /**
   * O ✕ que o autor deu no cartão do primeiro link do corpo (prévia de link): o link NORMALIZADO
   * (``https?://\S+``, até 2048). Ausente = sem ✕; nunca ``null`` (no PATCH ``null`` limparia o campo).
   */
  link_dispensado?: string;
}

/** Payload (parcial) de edição de post. */
export interface FeedPostUpdateInput {
  content?: string;
  attachments?: FeedAttachment[];
  visibility_scope?: FeedVisibilityScope;
  scope_target_id?: string | null;
}

export interface CreateCommentInput {
  content: string;
  parent_comment_id?: string | null;
  mentions?: FeedCommentMention[];
  /** O ✕ do autor no cartão do primeiro link do comentário (ver ``FeedPostCreateInput.link_dispensado``). */
  link_dispensado?: string;
}

const feedService = {
  async listPosts(params?: ListPostsParams): Promise<FeedPost[]> {
    const response = await api.get<FeedPost[]>(`${FEED_BASE}/posts`, {
      params,
    });
    return response.data;
  },

  /**
   * Um lote do "Carregar mais" (GET /feed/timeline): sem cursor, os destaques e
   * os ``limit`` comuns mais novos; com o ``proximo`` do lote anterior, os seguintes.
   */
  async listarLoteDaTimeline(
    params: { limit: number } & Partial<FeedCursor>
  ): Promise<FeedTimelineLote> {
    const response = await api.get<FeedTimelineLote>(`${FEED_BASE}/timeline`, { params });
    return response.data;
  },

  async getPost(id: string): Promise<FeedPost> {
    const response = await api.get<FeedPost>(`${FEED_BASE}/posts/${id}`);
    return response.data;
  },

  async createPost(payload: FeedPostCreateInput): Promise<FeedPost> {
    const response = await api.post<FeedPost>(`${FEED_BASE}/posts`, payload);
    return response.data;
  },

  async updatePost(id: string, payload: FeedPostUpdateInput): Promise<FeedPost> {
    const response = await api.patch<FeedPost>(`${FEED_BASE}/posts/${id}`, payload);
    return response.data;
  },

  /**
   * Fixa/desafixa um post num escopo (E17.2.2). ``scope='none'`` desafixa;
   * ``'global'`` (admin) e ``'sector'`` (gerente, com ``targetSectorId``). O
   * backend autoriza e aplica os limites (409 no limite, 403 sem permissão).
   */
  async setPin(id: string, scope: FeedPinScope, targetSectorId?: string | null): Promise<FeedPost> {
    const response = await api.patch<FeedPost>(`${FEED_BASE}/posts/${id}/pin`, {
      scope,
      target_sector_id: scope === "sector" ? targetSectorId : null,
    });
    return response.data;
  },

  /** Capacidades de pin do viewer + uso atual (E17.2.2). */
  async getPinContext(): Promise<FeedPinContext> {
    const response = await api.get<FeedPinContext>(`${FEED_BASE}/pin-context`);
    return response.data;
  },

  /**
   * Busca posts do feed (E17.2.2) via FTS do E3, respeitando a visibilidade do
   * viewer. Filtros: termo (q), autor, janela de datas, tag. Retorna posts
   * hidratados (mesmo shape da timeline), renderizáveis como cards.
   */
  async searchFeed(params: FeedSearchParams): Promise<FeedPost[]> {
    const response = await api.get<FeedPost[]>(`${FEED_BASE}/search`, {
      params: {
        q: params.q,
        author: params.author || undefined,
        date_from: params.date_from || undefined,
        date_to: params.date_to || undefined,
        tag: params.tag || undefined,
        limit: params.limit,
        offset: params.offset,
      },
    });
    return response.data;
  },

  /**
   * A busca em páginas (GET /feed/busca) para o "Carregar mais": mesmos filtros
   * do ``searchFeed``, e o ``total`` de resultados de verdade.
   */
  async buscarPagina(
    params: FeedSearchParams & { limit: number; offset: number }
  ): Promise<FeedBuscaPagina> {
    const response = await api.get<FeedBuscaPagina>(`${FEED_BASE}/busca`, {
      params: {
        q: params.q,
        author: params.author || undefined,
        date_from: params.date_from || undefined,
        date_to: params.date_to || undefined,
        tag: params.tag || undefined,
        limit: params.limit,
        offset: params.offset,
      },
    });
    return response.data;
  },

  /**
   * Hashtags em alta nas últimas 24h (E17.2.3) — agg de chat_message_hashtags
   * (infra do chat, E6.4.2), escopado por tenant, ``COUNT`` desc, top-``limit``
   * (default 10). Alimenta o carousel "Em alta" no sidebar do feed.
   */
  async getTrending(limit = 10): Promise<TrendingHashtag[]> {
    const response = await api.get<TrendingHashtag[]>(`${FEED_BASE}/trending`, {
      params: { limit },
    });
    return response.data;
  },

  async deletePost(id: string): Promise<void> {
    await api.delete(`${FEED_BASE}/posts/${id}`);
  },

  async toggleReaction(id: string, emoji: string): Promise<FeedReactionToggleResponse> {
    const response = await api.post<FeedReactionToggleResponse>(`${FEED_BASE}/posts/${id}/react`, {
      emoji,
    });
    return response.data;
  },

  /**
   * Quem reagiu ao post (passo 15): uma linha por reação (pessoa + emoji), em
   * ordem cronológica e SEM teto — "todos" é todos. Respeita a visibilidade do
   * post (post de setor alheio → 404).
   */
  async listReactions(postId: string): Promise<FeedReaction[]> {
    const response = await api.get<FeedReaction[]>(`${FEED_BASE}/posts/${postId}/reactions`);
    return response.data;
  },

  /**
   * Vota numa opção da enquete do post (E17.2.1 — escolha única, voto final).
   * Devolve o estado da enquete já com contagens reveladas (has_voted=true).
   * Sem WS: o hook atualiza o post no cache no onSuccess.
   */
  async votePoll(postId: string, optionId: string): Promise<Poll> {
    const response = await api.post<Poll>(`${FEED_BASE}/posts/${postId}/poll/vote`, {
      option_id: optionId,
    });
    return response.data;
  },

  /**
   * Faz upload de UM anexo (multipart) e devolve o ``FeedAttachment`` já com
   * ``kind`` derivado + signed url p/ preview. A ``url`` NÃO deve ser reenviada
   * no create do post (o backend recomputa no read).
   */
  async uploadAttachment(file: File): Promise<FeedAttachment> {
    const form = new FormData();
    form.append("file", file);
    const response = await api.post<FeedAttachment>(`${FEED_BASE}/posts/attachments`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  // --- Comentários (E17.1.2) ---------------------------------------------

  async listComments(postId: string): Promise<FeedComment[]> {
    const response = await api.get<FeedComment[]>(`${FEED_BASE}/posts/${postId}/comments`);
    return response.data;
  },

  async createComment(postId: string, payload: CreateCommentInput): Promise<FeedComment> {
    const response = await api.post<FeedComment>(`${FEED_BASE}/posts/${postId}/comments`, payload);
    return response.data;
  },

  async updateComment(postId: string, commentId: string, content: string): Promise<FeedComment> {
    const response = await api.patch<FeedComment>(
      `${FEED_BASE}/posts/${postId}/comments/${commentId}`,
      { content }
    );
    return response.data;
  },

  async deleteComment(postId: string, commentId: string): Promise<void> {
    await api.delete(`${FEED_BASE}/posts/${postId}/comments/${commentId}`);
  },

  /** Candidatos de @menção pra um comentário — só quem enxerga o post (E17.1.3). */
  async listMentionCandidates(postId: string, q: string): Promise<FeedMentionCandidate[]> {
    const response = await api.get<{ candidates: FeedMentionCandidate[] }>(
      `${FEED_BASE}/posts/${postId}/mention-candidates`,
      { params: { q, limit: 8 } }
    );
    return response.data?.candidates ?? [];
  },

  /**
   * Candidatos de @menção ao COMPOR um post novo (E17.3.2b) — ainda não há
   * post_id, então o conjunto depende do escopo escolhido (público → todos do
   * tenant; setor/grupo → membros). Espelha o anti-spoof do backend (zero 422).
   */
  async listComposeMentionCandidates(
    q: string,
    visibilityScope: FeedVisibilityScope,
    scopeTargetId: string | null
  ): Promise<FeedMentionCandidate[]> {
    const response = await api.get<{ candidates: FeedMentionCandidate[] }>(
      `${FEED_BASE}/mention-candidates`,
      {
        params: {
          q,
          limit: 8,
          visibility_scope: visibilityScope,
          ...(scopeTargetId ? { scope_target_id: scopeTargetId } : {}),
        },
      }
    );
    return response.data?.candidates ?? [];
  },

  /**
   * Quantos posts a pessoa ainda não viu e pode ver (os dela e os apagados não contam).
   * Alimenta o selo do "Feed da Empresa" no menu lateral.
   */
  async getNovidades(): Promise<NovidadesDoFeed> {
    const response = await api.get<NovidadesDoFeed>(`${FEED_BASE}/novidades`);
    return response.data;
  },

  /** Marca o feed como visto agora; o servidor devolve `{ total: 0 }`. */
  async marcarVisto(): Promise<NovidadesDoFeed> {
    const response = await api.post<NovidadesDoFeed>(`${FEED_BASE}/novidades/visto`);
    return response.data;
  },
};

export default feedService;
